import React from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import dayjs from 'dayjs';
// Import other MUI components and utilities as needed (e.g., Table, TableHead, etc.)

const CostReport = ({
  colors,
  reportData,
  headers,
  rows,
  units,
  dateFrom,
  dateTo,
  setDateFrom,
  setDateTo,
  handleDownloadAll,
  handleDownloadSingle,
}) => (
    <>
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
      <Typography variant="h4" color={colors.grey[100]}>
        Cost Report
      </Typography>
      <Box display="flex" gap={2} alignItems="center">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="From Date"
            value={dateFrom}
            onChange={(newValue) => setDateFrom(newValue)}
            format='DD/MM/YYYY'
            sx={{
              '& .MuiInputBase-root': {
                color: colors.grey[100],
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.grey[700]
                }
              }
            }}
          />
          <DatePicker
            label="To Date"
            value={dateTo}
            onChange={(newValue) => setDateTo(newValue)}
            format='DD/MM/YYYY'
            sx={{
              '& .MuiInputBase-root': {
                color: colors.grey[100],
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.grey[700]
                }
              }
            }}
          />
        </LocalizationProvider>
      </Box>

      <Button
        variant="contained"
        startIcon={<FileDownloadIcon />}
        onClick={handleDownloadAll}
        sx={{
          bgcolor: colors.entras_purple[100],
          '&:hover': {
            bgcolor: colors.entras_purple[200]
          }
        }}
      >
        Download All
      </Button>
    </Box>

    <Card sx={{ bgcolor: colors.primary[400], boxShadow: 3 }}>
      <TableContainer component={Paper} sx={{ bgcolor: 'transparent' }}>
        <Table>
          <Tooltip title={reportData[0]?.message?.metaData?.calculationTime?.description || "Report generated"}>
            <TableCell sx={{ fontWeight: "bold" }}>
              {headers[0] || "Report generated"}
            </TableCell>
          </Tooltip>

          <Tooltip title={reportData[0]?.message?.metaData?.calculationPeriodFromDate?.description || "Period from"}>
            <TableCell sx={{ fontWeight: "bold" }}>
              {headers[1] || "Period"}
            </TableCell>
          </Tooltip>

          <Tooltip title={reportData[0]?.message?.metaData?.calculationPeriodToDate?.description || "Period to"}>
            <TableCell sx={{ fontWeight: "bold" }}>
              {headers[2] || "Period to"}
            </TableCell>
          </Tooltip>

          <Tooltip title={reportData[0]?.message?.costs?.baseline?.description || "Baseline cost"}>
            <TableCell sx={{ fontWeight: "bold" }}>
              {headers[3] || "Baseline Cost"}
            </TableCell>
          </Tooltip>

          <Tooltip title={reportData[0]?.message?.costs?.optimised?.description || "Optimised cost"}>
            <TableCell sx={{ fontWeight: "bold" }}>
              {headers[4] || "Optimised Cost"}
            </TableCell>
          </Tooltip>

          <Tooltip title={reportData[0]?.message?.costs?.savings?.description || "Savings"}>
            <TableCell sx={{ fontWeight: "bold" }}>
              {headers[5] || "Savings"}
            </TableCell>
          </Tooltip>

          <Tooltip title={reportData[0]?.message?.marketPrices?.average?.description || "Baseload spot price"}>
            <TableCell sx={{ fontWeight: "bold" }}>
              {headers[6] || "Avg Price"}
            </TableCell>
          </Tooltip>

          <Tooltip title={reportData[0]?.message?.marketPrices?.profileWeighted?.description || "Optimised spot price"}>
            <TableCell sx={{ fontWeight: "bold" }}>
              {headers[7] || "Optimised Price"}
            </TableCell>
          </Tooltip>

          <Tooltip title={reportData[0]?.message?.energy?.totalConsumption?.description || "Total energy consumption"}>
            <TableCell sx={{ fontWeight: "bold" }}>
              {headers[8] || "Total Consumption"}
            </TableCell>
          </Tooltip>

          <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={index}>
                <TableCell sx={{ color: colors.grey[100] }}>
                  {dayjs(row[0]).format('DD-MM-YYYY HH:mm')}
                </TableCell>
                <TableCell sx={{ color: colors.grey[100] }}>
                  {`${dayjs(row[1]).format('DD-MM-YYYY')} `}
                </TableCell>
                <TableCell sx={{ color: colors.grey[100] }}>
                  {`${dayjs(row[2]).format('DD-MM-YYYY')} `}
                </TableCell>
                <TableCell sx={{ color: colors.grey[100] }}>
                  {`${row[3]?.toFixed(2)} ${units[3]}`}
                </TableCell>
                <TableCell sx={{ color: colors.grey[100] }}>
                  {`${row[4]?.toFixed(2)} ${units[4]}`}
                </TableCell>
                <TableCell sx={{ color: colors.grey[100] }}>
                  {`${row[5]?.toFixed(2)} ${units[5]}`}
                </TableCell>
                <TableCell sx={{ color: colors.grey[100] }}>
                  {`${row[6]?.toFixed(2)} ${units[6]}`}
                </TableCell>
                <TableCell sx={{ color: colors.grey[100] }}>
                  {`${row[7]?.toFixed(2)} ${units[7]}`}
                </TableCell>
                <TableCell sx={{ color: colors.grey[100] }}>
                  {`${row[8]?.toFixed(2)} ${units[8]}`}
                </TableCell>
                <TableCell>
                  <Tooltip title="Download Row">
                    <IconButton
                      onClick={() => handleDownloadSingle(row)}
                      sx={{ color: colors.grey[100] }}
                    >
                      <FileDownloadIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </>
);

export default CostReport;